import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { HTML } from '@adornis/cms/db/scalars.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { min } from '@adornis/validation/functions/range.js';
import { mongo } from '../server/mongo.js';
import { ColorDefinitions } from './ColorDefinitions.js';

export const GLOBAL_SETTINGS_ID = '_global_settings';

function getScope(setting: Setting) {
  if (setting.parentInstitutionID) return 'workshop';
  if (setting.institutionID) return 'institution';
  return 'global';
}

export async function getInheritedSetting(
  setting: Setting,
  propName: keyof Setting,
): Promise<[string, any] | undefined> {
  let parent = await getParent(setting);
  while (parent) {
    const value = parent[propName];
    const scope = getScope(parent);
    if (value != null) return [scope, value];
    else parent = await getParent(parent);
  }
}

function createInheritedPropsResolver(propName: keyof Setting) {
  return function (this: Setting) {
    return () => getInheritedSetting(this, propName);
  };
}

function createInheritedPropsOptions(propName: keyof Setting) {
  return { resolve: createInheritedPropsResolver(propName) };
}

const settingFieldDependencies = {
  classes: () => [Setting],
  ids: async function (this: Setting) {
    const scope = getScope(this);
    if (scope === 'global') return [];
    if (scope === 'institution') return getParent(this).then(parent => (parent ? [parent._id] : []));

    const parent = await getParent(this);
    if (!parent) return [];
    const parent2 = await getParent(parent);
    if (!parent2) return [parent._id];
    return [parent._id, parent2._id];
  },
};

async function getParent(setting: Setting): Promise<Setting | undefined> {
  if (setting.parentInstitutionID) {
    return (await loadMongoData(Setting, { institutionID: setting.parentInstitutionID })).at(0) as Setting;
  }

  if (setting.institutionID) {
    return (await getGlobalSettings()) as Setting;
  }

  return undefined;
}

@Entity()
export class SettingColorDefintionProp extends AdornisEntity {
  static override _class = 'SettingColorDefintionProp';

  @Field(type => String)
  scope!: string;

  @Field(type => ColorDefinitions)
  value: Maybe<ColorDefinitions>;
}

@Entity()
export class Setting extends MongoEntity {
  static override _class = 'Setting';
  static override _collectionName = 'settings';

  @Field(type => String)
  productID: Maybe<string>;

  @Field(type => String)
  parentInstitutionID: Maybe<string>;

  @Field(type => String)
  institutionID: Maybe<string>;

  @Field(type => HTML)
  courseLocation: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, HTML], createInheritedPropsOptions('courseLocation'))
  inheritedCourseLocation?: [string, string];

  @validate(min(1))
  @Field(type => Int)
  capacity?: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('capacity'))
  inheritedCapacity?: [string, number];

  @validate(
    and(min(1), options => {
      const firstValue = options.value;
      const secondValue =
        options.target.secondReminderLeadTime ?? parseInt(options.target.inheritedSecondReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (firstValue <= secondValue)
        throw new ValidationError('Erste Erinnerung muss vor der zweiten liegen', {
          translationKey: 'firstReminderBeforeSecond',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  firstReminderLeadTime?: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('firstReminderLeadTime'))
  inheritedFirstReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const secondValue = options.value;
      const firstValue =
        options.target.firstReminderLeadTime ?? parseInt(options.target.inheritedFirstReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (secondValue >= firstValue)
        throw new ValidationError('Erste Erinnerung muss nach der zweiten liegen', {
          translationKey: 'secondReminderAfterFirst',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  secondReminderLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('secondReminderLeadTime'))
  inheritedSecondReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const firstValue = options.value;
      const secondValue =
        options.target.secondAppointmentReminderLeadTime ??
        parseInt(options.target.inheritedSecondAppointmentReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (firstValue <= secondValue)
        throw new ValidationError('Erste Erinnerung muss vor der zweiten liegen', {
          translationKey: 'firstReminderBeforeSecond',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  firstAppointmentReminderLeadTime?: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('firstAppointmentReminderLeadTime'))
  inheritedFirstAppointmentReminderLeadTime?: [string, number];

  @validate(
    and(min(1), options => {
      const secondValue = options.value;
      const firstValue =
        options.target.firstAppointmentReminderLeadTime ??
        parseInt(options.target.inheritedFirstAppointmentReminderLeadTime?.[1]);
      if (!(typeof firstValue === 'number')) return;
      if (!(typeof secondValue === 'number')) return;
      if (secondValue >= firstValue)
        throw new ValidationError('Erste Erinnerung muss nach der zweiten liegen', {
          translationKey: 'secondReminderAfterFirst',
          key: options.key,
        });
    }),
  )
  @Field(type => Int)
  secondAppointmentReminderLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('secondAppointmentReminderLeadTime'))
  inheritedSecondAppointmentReminderLeadTime?: [string, number];

  @Field(type => Int)
  meetingInfoMailLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('meetingInfoMailLeadTime'))
  inheritedMeetingInfoMailLeadTime?: [string, number];

  @Field(type => Int)
  secondMeetingInfoMailLeadTimeHours: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('secondMeetingInfoMailLeadTimeHours'))
  inheritedSecondMeetingInfoMailLeadTimeHours?: [string, number];

  @Field(type => Int)
  einzelberatungMeetingInfoMailLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('einzelberatungMeetingInfoMailLeadTime'))
  inheritedEinzelberatungMeetingInfoMailLeadTime?: [string, number];

  @validate(min(1))
  @Field(type => Int)
  lecturerWorkshopReminderLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('lecturerWorkshopReminderLeadTime'))
  inheritedlecturerWorkshopReminderLeadTime?: [string, number];

  @validate(min(1))
  @Field(type => Int)
  lecturerEvaluationMailLeadTime: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('lecturerEvaluationMailLeadTime'))
  inheritedlecturerEvaluationMailLeadTime?: [string, number];

  @Field(type => ColorDefinitions)
  colorDefinitions: Maybe<ColorDefinitions>;

  // NOTE: type [string, SettingColorDefintionProp] does currently not work for some reason
  //       feel free to use tuple again when engine is fixed
  @Dependencies(settingFieldDependencies as any)
  @Field(type => SettingColorDefintionProp, {
    resolve: function (this: Setting) {
      return async () => {
        const res = await getInheritedSetting(this, 'colorDefinitions');
        if (!res) return null;
        const [scope, value] = res;
        return new SettingColorDefintionProp({ scope, value });
      };
    },
  })
  inheritedColorDefinitions?: SettingColorDefintionProp;

  @Field(type => [String])
  extraParticipationConstraints: Maybe<Array<string>>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('extraParticipationConstraints'))
  inheritedExtraParticipationConstraints?: [string, Array<string>];

  @validate(min(0))
  @Field(type => Int)
  blockSignOffDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('blockSignOffDaysInAdvance'))
  inheritedBlockSignOffDaysInAdvance?: [string, number];

  @validate(min(0))
  @Field(type => Int)
  blockAutomaticMoveUpDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('blockAutomaticMoveUpDaysInAdvance'))
  inheritedBlockAutomaticMoveUpDaysInAdvance?: [string, number];

  @validate(min(0))
  @Field(type => Int)
  blockSignInHoursInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('blockSignInHoursInAdvance'))
  inheritedBlockSignInHoursInAdvance?: [string, number];

  @validate(min(0))
  @Field(type => Int)
  blockActiveRegistrationHoursInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('blockActiveRegistrationHoursInAdvance'))
  inheritedBlockActiveRegistrationHoursInAdvance?: [string, number];

  @validate(min(1))
  @Field(type => Int)
  qrCodeValidityInMinutes: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('qrCodeValidityInMinutes'))
  inheritedQrCodeValidityInMinutes?: [string, number];

  @Field(type => String)
  interAccountingEmail1: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail1'))
  inheritedInterAccountingEmail1?: [string, string];

  @Field(type => String)
  interAccountingEmail2: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail2'))
  inheritedInterAccountingEmail2?: [string, string];

  @Field(type => String)
  interAccountingEmail3: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('interAccountingEmail3'))
  inheritedInterAccountingEmail3?: [string, string];

  @Field(type => String)
  sdContactEmail: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('sdContactEmail'))
  inheritedSdContactEmail?: [string, string];

  @Field(type => String)
  sdCentralAccountingEmail: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('sdCentralAccountingEmail'))
  inheritedSdCentralAccountingEmail?: [string, string];

  @Field(type => String)
  evaluationLink: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('evaluationLink'))
  inheritedEvaluationLink?: [string, string];

  @Field(type => String)
  evaluationLinkCode: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('evaluationLinkCode'))
  inheritedEvaluationLinkCode?: [string, string];

  @Field(type => Int)
  sendInvoiceDaysInAdvance: Maybe<number>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Int], createInheritedPropsOptions('sendInvoiceDaysInAdvance'))
  inheritedSendInvoiceDaysInAdvance?: [string, number];

  @validate(options => {
    if (options.value === undefined || options.value === null)
      void 0; // allow null-values
    else return minLength(1, 'PAYMENT.select_method')(options);
  })
  @Field(type => [String])
  paymentMethods?: string[];

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, [String]], createInheritedPropsOptions('paymentMethods'))
  inheritedPaymentMethods?: [string, string[]];

  @Field(type => String)
  replyToMailAddress: Maybe<string>;

  @Field(type => String)
  infoLink: Maybe<string>;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, String], createInheritedPropsOptions('infoLink'))
  inheritedInfoLink?: [string, string];

  @Field(type => Boolean, { default: v => v ?? true })
  autoNachrueckenEnabled!: boolean;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoNachrueckenEnabled'))
  inheritedAutoNachrueckenEnabled?: [string, boolean];

  @Field(type => Boolean, { default: v => v ?? false })
  autoSendCertifatesEnabled!: boolean;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoSendCertifatesEnabled'))
  inheritedAutoSendCertifatesEnabled?: [string, boolean];

  // Automatically move "verbindlich angemeldet" participations to no_show after 7 days
  @Field(type => Boolean, { default: v => v ?? false })
  autoMoveNoShowEnabled!: boolean;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Boolean], createInheritedPropsOptions('autoMoveNoShowEnabled'))
  inheritedAutoMoveNoShowEnabled?: [string, boolean];

  @Field(type => Boolean, { default: v => v ?? false })
  sendNewsletterEnabled!: boolean;

  @Dependencies(settingFieldDependencies as any)
  @Field(type => [String, Boolean], createInheritedPropsOptions('sendNewsletterEnabled'))
  inheritedSendNewsletterEnabled?: [string, boolean];

  @Field(type => Boolean, { default: v => v ?? false })
  allowSubscribingToWaitinglistWithoutMeetingRequirements!: boolean;

  @Dependencies(settingFieldDependencies as any)
  @Field(
    type => [String, Boolean],
    createInheritedPropsOptions('allowSubscribingToWaitinglistWithoutMeetingRequirements'),
  )
  inheritedAllowSubscribingToWaitinglistWithoutMeetingRequirements?: [string, boolean];

  static override async beforeCreate(entity: Setting): Promise<void> {
    trimSettingEmails(entity);
    return super.beforeCreate(entity);
  }

  static override async beforeUpdate(oldEntity: Setting, update: Setting): Promise<void> {
    trimSettingEmails(update);
    return super.beforeUpdate(oldEntity, update);
  }
}

const SETTING_EMAIL_FIELDS = [
  'interAccountingEmail1',
  'interAccountingEmail2',
  'interAccountingEmail3',
  'sdContactEmail',
  'sdCentralAccountingEmail',
  'replyToMailAddress',
] as const;

function trimSettingEmails(s: Partial<Setting>) {
  const doc = (s as any).doc;
  if (!doc) return;
  for (const key of SETTING_EMAIL_FIELDS) {
    const v = doc[key];
    if (typeof v === 'string') (s as any)[key] = v.trim();
  }
}

/**
 * we optimize the hot path for go-live in April 2026
 * However, in reality we should rewrite settings to be cached in materialized view better.
 * There's also already an aggregation at the bottom of this file to collect settings more efficiently. Needs a proper re-engineering.
 */
let lastFetchDate = performance.now();
let lastFetchedData: EntityData<Setting> | null = null;
export const getGlobalSettings = async () => {
  if (performance.now() - lastFetchDate < 1000 && lastFetchedData != null) {
    return lastFetchedData;
  }
  lastFetchDate = performance.now();
  lastFetchedData = (await loadMongoData(Setting, GLOBAL_SETTINGS_ID)) as EntityData<Setting>;
  return lastFetchedData;
};

/**
 * Creates MongoDB aggregation pipeline stages to lookup settings for a product with typesafe field access.
 * Supports inheritance from institution to global settings using a single lookup with pipeline.
 */
export const lookupSettingsForProduct =
  (config: { path: string[]; as: string }) =>
  <TFields extends ReadonlyArray<keyof Setting>>(fields: TFields) => {
    const productPath = config.path.length > 0 ? config.path.join('.') + '.' : '';
    const settingsAlias = config.as;

    return [
      {
        $lookup: {
          from: Setting._collectionName,
          as: 'all_settings',
          let: { productID: `$${productPath}_id`, institutionID: `$${productPath}institutionID` },
          pipeline: [
            {
              $match: {
                $or: [
                  // Product-specific settings (highest priority)
                  { $expr: { $eq: ['$productID', '$$productID'] } },
                  // Institution settings (fallback)
                  { $expr: { $eq: ['$institutionID', '$$institutionID'] } },
                  // Global settings (final fallback)
                  {
                    $and: [
                      { $expr: { $eq: ['$productID', null] } }, //
                      { $expr: { $eq: ['$institutionID', null] } },
                    ],
                  },
                ],
              },
            },
            {
              $addFields: {
                priority: {
                  $cond: {
                    if: { $ne: ['$productID', null] },
                    then: 1, // Product settings have highest priority
                    else: {
                      $cond: {
                        if: { $ne: ['$institutionID', null] },
                        then: 2, // Institution settings have medium priority
                        else: 3, // Global settings have lowest priority
                      },
                    },
                  },
                },
              },
            },
            { $sort: { priority: 1 } },
          ],
        },
      },
      {
        $set: {
          [settingsAlias]: fields.reduce(
            (acc, field) => {
              acc[field as string] = {
                $let: {
                  vars: {
                    foundSetting: {
                      $arrayElemAt: [
                        { $filter: { input: '$all_settings', cond: { $ne: [`$$this.${field as string}`, null] } } },
                        0,
                      ],
                    },
                  },
                  in: `$$foundSetting.${field as string}`,
                },
              };
              return acc;
            },
            {} as Record<string, any>,
          ),
        },
      },
      { $unset: 'all_settings' },
    ] as const;
  };

export async function getMergedProductSettings(
  productID: string,
  fields: (keyof Setting)[],
): Promise<EntityData<Setting>> {
  const { Product } = await import('./products.js');
  const productCollection = mongo().collection(Product._collectionName);
  const result = await productCollection
    .aggregate([{ $match: { _id: productID } }, ...lookupSettingsForProduct({ path: [], as: 'settings' })(fields)])
    .toArray();

  if (!result.length) throw new Error('Product Settings not found for product: ' + productID);
  return result.at(0)!.settings;
}
